import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { UploadIcon } from 'tdesign-icons-react';
import {
  Button,
  Form,
  Link,
  MessagePlugin,
  Popconfirm,
  Select,
  Space,
  Table,
  Upload,
} from 'tdesign-react';
import { isEmpty } from 'lodash';
import { renderDateTime, isVoid, toTsOptions } from '@/utils/common';
import { fetchComponentList } from '@/slices/templateSlice';
import { deleteComponentLib, getComponentLibList, libUpload } from '@/services/resourceService';
import useTable from '@/hooks/useTable';
import Panel from '@/components/Panel';
import PermWrapper from '@/components/PermWrapper';
import MannualUpdateVersion from '../components/MannualUpdateVersion';
import AutoUpdateVersion from '../components/AutoUpdateVersion';
import AutoUpdateAllVersion from '../components/AutoUpdateAllVersion';

const { FormItem } = Form;

function EnvManage({ fetchComponentList, options }) {
  const { productLibId } = useParams();
  // 保证js时序
  const [operators, setOperators] = useState([]);

  const columns = [
    {
      colKey: 'productName',
      title: '组件名称',
      width: 200,
    },
    {
      colKey: 'path',
      title: '文件',
    },
    {
      colKey: 'version',
      title: '版本',
      width: 100,
    },
    {
      colKey: 'updateTime',
      title: '日期',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <PermWrapper resource='pulsar:resource:env:zpmanage:delete'>
          <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
            <Link theme='danger' hover='color'>
              删除
            </Link>
          </Popconfirm>
        </PermWrapper>
      ),
      width: 80,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getComponentLibList({
      ...params,
      envId: 0,
      clusterId: 0,
      productLibId,
    });
    setData(data);
  });

  const doUpload = useMemoizedFn(async (file) => {
    if (isVoid(componentStencilId)) {
      MessagePlugin.warning('请先选择组件');
      return;
    }
    if (file[0]) {
      const formData = new FormData();
      formData.append('file', file[0]);
      formData.append('componentId', componentStencilId);
      formData.append('productLibId', productLibId);
      let uploadMsg;
      try {
        uploadMsg = MessagePlugin.loading({
          duration: 0,
          content: '正在上传...',
        });
        await libUpload(formData);
        MessagePlugin.success('上传成功！');
        doRefresh();
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        MessagePlugin.close(uploadMsg);
      }
    }
  });

  const { header, config, setData, doRefresh, form, doSearch } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <>
        <FormItem name='componentStencilId'>
          <Select
            options={toTsOptions(options)}
            placeholder='选择组件'
            popupProps={{ overlayInnerStyle: { width: 'auto', minWidth: 200 } }}
          />
        </FormItem>
        {operators[0]}
      </>
    ),
    searchButton: null,

    operators: operators[1],
  });

  const componentStencilId = Form.useWatch('componentStencilId', form);

  const doDelete = async ({ id }) => {
    try {
      await deleteComponentLib(id);
      MessagePlugin.success('删除成功');
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    }
  };

  useEffect(() => {
    fetchComponentList({ startIndex: 1, pageSize: 99999 });
  }, [fetchComponentList]);

  // useTable 中使用，相互依赖，保障js初始化时序
  useEffect(() => {
    if (isVoid(componentStencilId)) {
      setOperators([]);
      return;
    }

    const leftButtons = (
      <Space size='small'>
        <PermWrapper resource='pulsar:resource:env:zpmanage:update'>
          <MannualUpdateVersion
            productLibId={productLibId}
            componentStencilId={componentStencilId}
            onFinish={doRefresh}
          />
        </PermWrapper>
        <PermWrapper resource='pulsar:resource:env:zpmanage:autoUpdate'>
          <AutoUpdateVersion
            productLibId={productLibId}
            componentStencilId={componentStencilId}
            onFinish={doRefresh}
          />
        </PermWrapper>
        <PermWrapper resource='pulsar:resource:env:zpmanage:upload'>
          {' '}
          <Upload theme='file' onSelectChange={doUpload}>
            <Button theme='primary' variant='outline' icon={<UploadIcon />}>
              上传制品
            </Button>
          </Upload>
        </PermWrapper>
      </Space>
    );

    const rightButtons = (
      <PermWrapper resource='pulsar:resource:env:zpmanage:autoUpdateAll'>
        <AutoUpdateAllVersion
          productLibId={productLibId}
          componentStencilId={componentStencilId}
          onFinish={doRefresh}
        />
      </PermWrapper>
    );

    setOperators([leftButtons, rightButtons]);
  }, [componentStencilId, doRefresh, productLibId, doUpload]);

  useEffect(() => {
    if (!isEmpty(options)) {
      const { value } = options[0];
      form.setFields([{ name: 'componentStencilId', value }]);
    }
  }, [options, form]);

  useEffect(() => {
    if (componentStencilId) {
      doSearch({ componentStencilId });
    }
  }, [componentStencilId, doSearch]);

  return (
    <Root>
      <Panel>
        {header}
        <Table {...config} />
      </Panel>
    </Root>
  );
}

export default connect(
  (state) => ({
    options: state.template.components.map((item) => ({
      ...item,
      value: item.id,
    })),
  }),
  {
    fetchComponentList,
  },
)(EnvManage);

const Root = styled.div`
  padding-right: 20px;
`;
